import React, { useState, useEffect, useRef, useCallback, useMemo, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { AuthContext } from '../App';
import { readTime, statusTag, isExperiment } from '../lib/meta';
import { EvidenceLabel } from './EvidenceLabel';
import {
  Search, BookOpen, FlaskConical, FileText, Layers, Bell, CornerDownLeft,
  ArrowUp, ArrowDown, Command, Compass, ShieldCheck, Archive,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RECENT_KEY = 'fff_recent_records';

const KIND_META = {
  log: { Icon: BookOpen, label: 'Field Log' },
  experiment: { Icon: FlaskConical, label: 'Experiment' },
  blueprint: { Icon: FileText, label: 'Blueprint' },
  domain: { Icon: Layers, label: 'Domain' },
  update: { Icon: Bell, label: 'Field Update' },
  command: { Icon: Compass, label: 'Action' },
};

const SCOPES = [
  { id: 'all', label: 'All' },
  { id: 'log', label: 'Logs' },
  { id: 'experiment', label: 'Experiments' },
  { id: 'blueprint', label: 'Blueprints' },
  { id: 'domain', label: 'Domains' },
  { id: 'update', label: 'Updates' },
];

const fuzzyScore = (q, text) => {
  if (!text) return 0;
  const t = text.toLowerCase();
  const query = q.toLowerCase().trim();
  if (!query) return 0;
  if (t === query) return 100;
  if (t.startsWith(query)) return 82;
  const idx = t.indexOf(query);
  if (idx >= 0) return 64 - Math.min(idx, 20);
  let qi = 0;
  for (let i = 0; i < t.length && qi < query.length; i += 1) {
    if (t[i] === query[qi]) qi += 1;
  }
  return qi === query.length ? 24 : 0;
};

// Cross-concept bridges (mirror of the backend engine) — lets the search behave
// semantically: querying "resonance" can surface conductivity records, etc.
const BRIDGES = [
  [['resonance', 'resonant', 'frequency', 'vibration', 'acoustic'], ['conductivity', 'conductive', 'conductance', 'ion']],
  [['atmospheric', 'ionosphere', 'ionization', 'corona', 'discharge'], ['electroculture', 'plant', 'crop', 'agricultur', 'soil']],
  [['water', 'hydro', 'moisture', 'rain'], ['mineral', 'conductivity', 'conductive', 'ion', 'salinity']],
  [['magnet', 'magnetic', 'magnetism', 'geomagnet'], ['material', 'lattice', 'crystal', 'microstructure']],
  [['plasma', 'ionization', 'corona', 'discharge', 'glow'], ['atmospheric', 'atmosphere', 'air', 'energy']],
  [['off-grid', 'offgrid', 'passive', 'autonomous', 'standalone'], ['infrastructure', 'sustainable', 'solar', 'harvesting']],
];

// Semantic boost: if the query matches one side of a bridge and the record's
// blob matches the other side, surface it (below direct keyword matches).
const semanticScore = (q, blob) => {
  const query = q.toLowerCase().trim();
  if (!query || !blob) return 0;
  const b = blob.toLowerCase();
  for (const [sa, sb] of BRIDGES) {
    const qa = sa.some((k) => query.includes(k));
    const qb = sb.some((k) => query.includes(k));
    if (qa && sb.some((k) => b.includes(k))) return 40;
    if (qb && sa.some((k) => b.includes(k))) return 40;
  }
  return 0;
};

const scoreItem = (q, item) => {
  const titleS = fuzzyScore(q, item.title) * 1.6;
  const metaS = Math.max(
    fuzzyScore(q, item.description || ''),
    fuzzyScore(q, item.content_type || ''),
    fuzzyScore(q, item.status || ''),
    fuzzyScore(q, item.domainName || ''),
    fuzzyScore(q, item.keywords || ''),
    fuzzyScore(q, (item.related || []).join(' ')),
    fuzzyScore(q, (item.references || []).join(' ')),
  );
  return Math.max(titleS, metaS, semanticScore(q, item.blob || item.keywords || ''));
};

const ResultRow = ({ item, active, onSelect, onHover, testid }) => {
  const meta = KIND_META[item.kind] || KIND_META.command;
  const Icon = meta.Icon;
  const status = item.status ? statusTag(item.status) : null;
  return (
    <button
      type="button"
      onMouseMove={onHover}
      onClick={() => onSelect(item)}
      data-testid={testid}
      className={`w-full text-left flex items-start gap-4 px-4 py-3 rounded-lg border-l-2 transition-colors ${active ? 'bg-[#1E8F89]/8 border-l-[#1E8F89]' : 'border-l-transparent hover:bg-white/[0.025]'}`}
    >
      <div className={`w-9 h-9 rounded-lg border flex items-center justify-center shrink-0 mt-0.5 ${active ? 'border-[#1E8F89]/50 bg-[#1E8F89]/10' : 'border-[#262A36] bg-[#0c0f17]'}`}>
        <Icon className={`w-4 h-4 ${active ? 'text-[#26B0A8]' : 'text-[#6B7280]'}`} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="mono-meta text-[#1E8F89]">{item.content_type || meta.label}</span>
          {item.domainName && <span className="mono-meta">· {item.domainName}</span>}
          {status && <span className={`tag ${status.cls}`}>{status.label}</span>}
          {item.evidence_class && <EvidenceLabel evidenceClass={item.evidence_class} />}
        </div>
        <p className={`font-sans-tech text-sm truncate ${active ? 'text-white' : 'text-[#C8CDD6]'}`}>{item.title}</p>
        {item.description && <p className="font-sans-tech text-xs text-[#6B7280] truncate mt-0.5">{item.description}</p>}
      </div>
      <div className="shrink-0 flex items-center gap-2 pt-1">
        {item.readMin ? <span className="mono-meta">{item.readMin} min</span> : null}
        {active && <CornerDownLeft className="w-3.5 h-3.5 text-[#26B0A8]" />}
      </div>
    </button>
  );
};

export const CommandPalette = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState('all');
  const [activeIndex, setActiveIndex] = useState(0);
  const [data, setData] = useState({ cats: [], arts: [], pdfs: [], ups: [] });
  const [recents, setRecents] = useState(() => {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch { return []; }
  });

  const inputRef = useRef(null);
  const listRef = useRef(null);
  const openRef = useRef(false);
  const loadedRef = useRef(false);

  const loadData = useCallback(async () => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    try {
      const reqs = [axios.get(`${API}/categories`)];
      if (user?.subscription_status === 'active') {
        reqs.push(axios.get(`${API}/articles`), axios.get(`${API}/pdfs`), axios.get(`${API}/updates`));
      }
      const res = await Promise.all(reqs);
      setData({
        cats: res[0]?.data || [],
        arts: res[1]?.data || [],
        pdfs: res[2]?.data || [],
        ups: res[3]?.data || [],
      });
    } catch (e) {
      loadedRef.current = false;
    }
  }, [user]);

  const openPalette = useCallback(() => { setOpen(true); setQuery(''); setScope('all'); setActiveIndex(0); loadData(); }, [loadData]);
  const closePalette = useCallback(() => { setOpen(false); }, []);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (openRef.current) closePalette(); else openPalette();
      }
    };
    const evtOpen = () => openPalette();
    window.addEventListener('keydown', handler);
    window.addEventListener('fff-open-palette', evtOpen);
    return () => {
      window.removeEventListener('keydown', handler);
      window.removeEventListener('fff-open-palette', evtOpen);
    };
  }, [openPalette, closePalette]);

  useEffect(() => {
    openRef.current = open;
    if (open) {
      document.body.style.overflow = 'hidden';
      const t = setTimeout(() => inputRef.current?.focus(), 40);
      return () => clearTimeout(t);
    }
    document.body.style.overflow = '';
    return undefined;
  }, [open]);

  const catName = useCallback((cid) => data.cats.find((c) => c.id === cid)?.name || '', [data.cats]);

  // Build the searchable index
  const index = useMemo(() => {
    const items = [];
    data.arts.forEach((a) => {
      const dn = catName(a.category_id);
      const concepts = (a.concepts || []).join(' ');
      items.push({
        key: `a-${a.id}`,
        kind: isExperiment(a) ? 'experiment' : 'log',
        title: a.title,
        description: a.description,
        domainName: dn,
        status: a.status,
        content_type: a.content_type,
        evidence_class: a.evidence_class,
        readMin: readTime(a.content),
        related: a.related_domains,
        references: a.references,
        keywords: `${a.content_type} ${a.status} ${a.difficulty} ${dn} ${concepts}`,
        blob: `${a.title} ${a.description} ${dn} ${concepts}`,
        navTo: `/article/${a.id}`,
      });
    });
    data.pdfs.forEach((p) => {
      const dn = catName(p.category_id);
      const concepts = (p.concepts || []).join(' ');
      items.push({
        key: `p-${p.id}`,
        kind: 'blueprint',
        title: p.title,
        description: p.description,
        domainName: dn,
        content_type: 'Blueprint',
        keywords: `blueprint pdf ${dn} ${p.complexity || ''} ${concepts}`,
        blob: `${p.title} ${p.description} ${dn} ${concepts}`,
        navTo: '/dashboard?tab=vault',
      });
    });
    data.ups.forEach((u) => {
      items.push({
        key: `u-${u.id}`,
        kind: 'update',
        title: u.title,
        description: u.description,
        content_type: 'Field Update',
        keywords: 'field update transmission latest',
        navTo: '/dashboard?tab=updates',
      });
    });
    data.cats.forEach((c) => {
      items.push({
        key: `c-${c.id}`,
        kind: 'domain',
        title: c.name,
        description: c.description,
        content_type: 'Domain',
        keywords: `domain ${c.name}`,
        navTo: `/dashboard?domain=${encodeURIComponent(c.name)}`,
      });
    });
    return items;
  }, [data, catName]);

  const commands = useMemo(() => ([
    { key: 'cmd-exp', kind: 'command', title: 'Open experimental items', content_type: 'Action', keywords: 'experiments experimental in progress', navTo: '/dashboard?tab=experiments' },
    { key: 'cmd-vault', kind: 'command', title: 'Open Blueprint Vault', content_type: 'Action', keywords: 'blueprint vault pdf downloads', navTo: '/dashboard?tab=vault' },
    { key: 'cmd-updates', kind: 'command', title: 'Open latest field notes', content_type: 'Action', keywords: 'latest field updates notes transmissions', navTo: '/dashboard?tab=updates' },
    { key: 'cmd-verified', kind: 'command', title: 'Show Reviewed records', content_type: 'Filter', keywords: 'reviewed verified status', action: 'setQuery', value: 'verified', AltIcon: ShieldCheck },
    { key: 'cmd-archived', kind: 'command', title: 'Show archived theories', content_type: 'Filter', keywords: 'archived theory theoretical', action: 'setQuery', value: 'archived theory', AltIcon: Archive },
  ]), []);

  const results = useMemo(() => {
    const q = query.trim();
    let list;
    if (!q) {
      const recentItems = recents.map((r) => ({ ...r })).slice(0, 5);
      list = [...recentItems, ...commands, ...index.filter((i) => i.kind === 'domain')];
    } else {
      const scored = [...index, ...commands]
        .map((it) => ({ it, s: scoreItem(q, it) }))
        .filter((x) => x.s > 0)
        .sort((a, b) => b.s - a.s)
        .map((x) => x.it);
      list = scored;
    }
    if (scope !== 'all') list = list.filter((i) => i.kind === scope);
    const seen = new Set();
    const deduped = [];
    list.forEach((i) => { if (!seen.has(i.key)) { seen.add(i.key); deduped.push(i); } });
    return deduped.slice(0, 14);
  }, [query, scope, index, commands, recents]);

  useEffect(() => {
    if (listRef.current) {
      const el = listRef.current.querySelector(`[data-idx="${activeIndex}"]`);
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex, results.length]);

  const pushRecent = (item) => {
    if (!item.navTo) return;
    const entry = {
      key: item.key, kind: item.kind, title: item.title, description: item.description,
      domainName: item.domainName, status: item.status, content_type: item.content_type,
      evidence_class: item.evidence_class, readMin: item.readMin, navTo: item.navTo,
    };
    const next = [entry, ...recents.filter((r) => r.key !== item.key)].slice(0, 6);
    setRecents(next);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  };

  const onSelect = (item) => {
    if (item.action === 'setQuery') {
      setQuery(item.value);
      setScope('all');
      setActiveIndex(0);
      inputRef.current?.focus();
      return;
    }
    pushRecent(item);
    setOpen(false);
    navigate(item.navTo);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); closePalette(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex((i) => Math.min(i + 1, results.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex((i) => Math.max(i - 1, 0)); return; }
    if (e.key === 'Enter') {
      e.preventDefault();
      const item = results[Math.min(activeIndex, results.length - 1)];
      if (item) onSelect(item);
    }
  };

  const onQueryChange = (e) => { setQuery(e.target.value); setActiveIndex(0); };
  const onScope = (s) => { setScope(s); setActiveIndex(0); inputRef.current?.focus(); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-start justify-center px-4 pt-[12vh]"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
          data-testid="command-palette"
        >
          <div className="absolute inset-0 bg-[#050507]/80 backdrop-blur-md" onClick={closePalette} />
          <motion.div
            initial={{ opacity: 0, y: -16, scale: 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.99 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-2xl glass-hud rounded-2xl overflow-hidden scanlines reflect-top"
          >
            {/* Input */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-[#262A36]">
              <Search className="w-4 h-4 text-[#1E8F89]" />
              <input
                ref={inputRef}
                value={query}
                onChange={onQueryChange}
                onKeyDown={onKeyDown}
                placeholder="Search the research archive…"
                className="flex-1 bg-transparent text-white placeholder-[#4b5563] font-sans-tech text-[15px] focus:outline-none"
                data-testid="palette-input"
              />
              <kbd className="mono-meta border border-[#262A36] rounded px-1.5 py-0.5">ESC</kbd>
            </div>

            {/* Scopes */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[#262A36] overflow-x-auto">
              {SCOPES.map((s) => (
                <button key={s.id} onClick={() => onScope(s.id)} className={`tech-chip !py-1.5 !px-3 ${scope === s.id ? 'tech-chip-active' : ''}`} data-testid={`palette-scope-${s.id}`}>{s.label}</button>
              ))}
            </div>

            {/* Results */}
            <div ref={listRef} className="max-h-[52vh] overflow-y-auto py-2 px-2" data-testid="palette-results">
              {!query && recents.length > 0 && (
                <p className="mono-meta px-3 pt-2 pb-1">Recently opened</p>
              )}
              {results.length === 0 ? (
                <div className="px-4 py-14 text-center">
                  <p className="font-sans-tech text-sm text-[#6B7280]">No records match “{query}”.</p>
                </div>
              ) : (
                results.map((item, i) => (
                  <div key={item.key} data-idx={i}>
                    <ResultRow
                      item={item}
                      active={i === activeIndex}
                      onSelect={onSelect}
                      onHover={() => setActiveIndex(i)}
                      testid={`palette-result-${i}`}
                    />
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-5 py-3 border-t border-[#262A36]">
              <div className="flex items-center gap-4 mono-meta">
                <span className="flex items-center gap-1"><ArrowUp className="w-3 h-3" /><ArrowDown className="w-3 h-3" /> navigate</span>
                <span className="flex items-center gap-1"><CornerDownLeft className="w-3 h-3" /> open</span>
                <span className="flex items-center gap-1"><span className="border border-[#262A36] rounded px-1">esc</span> close</span>
              </div>
              <span className="flex items-center gap-1.5 mono-meta"><Command className="w-3 h-3" /> Research Index</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CommandPalette;
